package ui.report.campaign

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp
import api.CampaignState
import api.CampaignType
import com.netguru.multiplatform.charts.line.LineChartPoint
import common.frontend.api.Io
import common.frontend.api.IoReport
import kotlinx.datetime.*
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.char
import org.ssoft.common.tools.EMPTY
import provider.CommonDisplayProvider
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.global_months_short
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldRegularDate
import tools.ImpressionCalculator
import tools.await
import tools.niceFormat
import ui.campaign.labelRes
import kotlin.math.roundToInt
import kotlin.math.roundToLong
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Instant

class ReportCampaignScope private constructor(
    val args: ReportCampaignArgs,
    val vm: ReportCampaignVM,
    val nav: Navigation,
    val display: CommonDisplayProvider,
) {

    init {
        vm.init(this)
    }

    companion object {

        @Composable
        fun remember(): ReportCampaignScope {
            val nav = LocalNavigation.current
            val args = LocalNavigationArgs.current.rememberArgs(ReportCampaignArgs::class)
            val vm = nav.viewModel(ReportCampaignVM::class, args.campaignGroupId.toString())
            val display = LocalDisplayProvider.current
            return remember(args.campaignGroupId) {
                ReportCampaignScope(args, vm, nav, display)
            }
        }
    }

    class Wrapper(
        val data: Io.ReportCampaign,
        val impression: IoReport.ReportImpression,
        val oh: MutableList<LocationOhPreview>
    ) {

        val isMultipleDays = impression.stamp.maxOrNull()?.let { max ->
            impression.stamp.minOrNull()?.let { min ->
                (max.milliseconds - min.milliseconds).inWholeDays > 1
            }
        } == true

        val primary = data.campaign.first { it.id == data.actualCampaignId }

        val state = CampaignState[primary.state]

        val type = CampaignType[primary.settings.type]

        val stateString = state.labelRes()

        val chart = Chart(impression)

        val inProgress = state !in arrayOf(CampaignState.STOPPED, CampaignState.FINISHED)

        val name = primary.name

        val start = primary.start

        val end = primary.end.takeIf { primary.stop.date == LocalDate.EMPTY }
            ?: primary.stop

        val medialSpace = (primary.settings.count * primary.settings.duration)
            .toDouble()
            .let { it / 1.hours.inWholeMilliseconds.toDouble() }
            .let { it * 100.0 }
            .roundToInt()

        var devices = data.campaign
            .map { it.devices }
            .flatten()
            .distinctBy { it.id }
            private set

        val devicesByLocation = devices
            .groupBy { it.id_location }
            .map {
                val locName = data.locations.firstOrNull { loc -> loc.id == it.key }?.name ?: "-"
                locName to it.value
            }

        var resources = data.campaign.map { it.visuals.map { it.resources } }
            .flatten()
            .flatten()
            .distinctBy { it.id }
            private set

        val impressionByDevice = buildMap {
            devices.forEach { device ->
                put(device.id, data.counter.devices.filter { it.id == device.id }.sumOf { it.counter })
            }
        }

        val impressionByResource = buildMap {
            resources.forEach { resource ->
                put(resource.id, data.counter.resource.filter { it.id == resource.id }.sumOf { it.counter })
            }
        }

        val impressionsMax = primary.impressionExpected

        val impressionsExpect = (impressionsMax.toDouble() * 0.95).roundToLong()

        val impressionsReal = impressionByResource.values.sum()


        val impressionAll = ImpressionCalculator(
            containsCommercial = primary.isCommercial,
            containsNonCommercial = primary.isNonCommercial,
            real = impressionsReal,
            targetMax = impressionsMax,
            start = primary.start.date,
            end = primary.end.date
        )

//        val labels: List<String>

//        init {
//            val diff: Long
//            fun Long.toDate(): LocalDate {
//                return Instant.fromEpochMilliseconds(this)
//                    .toLocalDateTime(TimeZone.currentSystemDefault())
//                    .date
//            }
//            if (impression.stamp.size > 1) {
//                diff = impression.stamp.max() - impression.stamp.min()
//                val dateFormatH = LocalDate.Format {
//                    dayOfMonth()
//                    char('.')
//                    monthNumber()
//                    char('.')
//                    char('\n')
//                    year()
//                }
//                val dateFormatV = LocalDate.Format {
//                    dayOfMonth()
//                    char('.')
//                    monthNumber()
//                    char('.')
//                }
//
//
//                var current = impression.stamp.min().toDate()
//                var end = impression.stamp.max().toDate()
//                val horizontal = diff.milliseconds < 10.days
//                val increment = diff.milliseconds.inWholeDays.let {
//                    if(it> 25) it / 20  else 1
//                }
//
//                val formatter = if (horizontal) dateFormatH else dateFormatV
//
//                labels = buildList {
//                    while (current < end) {
//                        add(current.format(formatter))
//                        current = current.plus(increment, DateTimeUnit.DAY)
//                    }
//                    add(end.format(formatter))
//                }
//                println("labels")
//                println(labels.joinToString())
//            } else {
//                diff = 0
//                labels = if (impression.stamp.isEmpty()) listOf()
//                else listOf(Instant.fromEpochMilliseconds(impression.stamp.first()).toLocalDateTime(TimeZone.currentSystemDefault()).date.toString())
//            }

//        }

        init {
            if (!inProgress && impressionsReal > 0) {
                resources = resources.filter { (impressionByResource[it.id] ?: 0) > 0 }
                devices = devices.filter { (impressionByDevice[it.id] ?: 0) > 0 }
            }
        }

        data class Chart(val impression: IoReport.ReportImpression) {

            val start = impression.stamp.firstOrNull()
                ?.let { stamp ->
                    Instant.fromEpochMilliseconds(stamp)
                        .toLocalDateTime(TimeZone.currentSystemDefault())
                        .date
                } ?: LocalDate.EMPTY

            val end = impression.stamp.lastOrNull()
                ?.let { stamp ->
                    Instant.fromEpochMilliseconds(stamp)
                        .toLocalDateTime(TimeZone.currentSystemDefault())
                        .date
                } ?: LocalDate.EMPTY

            private var _limitStart = start
            val limitStart = EFieldRegularDate(::_limitStart)

            private var _limitEnd = end
            val limitEnd = EFieldRegularDate(::_limitEnd)

            private val allPoints = buildList {
                var lastStamp = -1L
                val hour = 1.hours.inWholeMilliseconds

                impression.counter.indices.forEach { index ->
                    if (index == 0) {
                        add(
                            LineChartPoint(
                                x = impression.stamp[index],
                                y = impression.counter[index].toFloat(),
                            )
                        )
                        lastStamp = impression.stamp[index]
                    } else {
                        while (impression.stamp[index] > (lastStamp + hour)) {
                            lastStamp += hour
                            add(
                                LineChartPoint(
                                    x = lastStamp,
                                    y = 0F,
                                )
                            )
                        }
                        add(
                            LineChartPoint(
                                x = impression.stamp[index],
                                y = impression.counter[index].toFloat(),
                            )
                        )
                        lastStamp = impression.stamp[index]
                    }

                }
            }

            var data: List<LineChartPoint> by mutableStateOf(listOf())
                private set

            var overlayHeaderLabel: (@Composable (value: Any) -> Unit) by mutableStateOf({})
                private set

            var xAxisLabel: (@Composable (value: Any) -> Unit) by mutableStateOf({})
                private set


            suspend fun reset() {
                limitStart.set(start)
                limitEnd.set(end)
                rebuild()
            }

            suspend fun rebuild() {
                if (allPoints.isEmpty()) {
                    data = listOf()
                    overlayHeaderLabel = {}
                } else {
                    val monthNames = Res.string.global_months_short.await()
                        .split(",")
                        .let { MonthNames(it) }

                    val formatterHourly = LocalDateTime.Format {
                        year()
                        char(' ')
                        monthName(monthNames)
                        char(' ')
                        day()
                        char(' ')
                        hour()
                        chars(":00")
                    }

                    val formatterHourlyLabelX = LocalDateTime.Format {
                        monthName(monthNames)
                        char(' ')
                        day()
                        char('\n')
                        hour()
                        chars(":00")
                    }

                    val formatterDaily = LocalDateTime.Format {
                        year()
                        char(' ')
                        monthName(monthNames)
                        char(' ')
                        day()
                    }

                    val formatterWeekly = LocalDateTime.Format {
                        year()
                        char(' ')
                        monthName(monthNames)
                        char(' ')
                        day()
                    }

                    val formatterTime = LocalDateTime.Format {
                        hour()
                        chars(":00")
                    }

                    val formatterDaySingle = LocalDateTime.Format {
                        day()
                    }

                    val limitRange = limitStart.state
                        .toEpochDays()
                        .let { it.days - 1.days }
                        .inWholeMilliseconds
                        .let { lStart ->
                            limitEnd.state
                                .toEpochDays()
                                .let { it.days + 1.days }
                                .inWholeMilliseconds
                                .let { lEnd ->
                                    lStart..lEnd
                                }
                        }

                    val points = allPoints.filter {
                        it.x in limitRange
                    }

                    val start = points.firstOrNull()?.x?.milliseconds ?: 0.milliseconds
                    val end = points.lastOrNull()?.x?.milliseconds ?: 0.milliseconds
                    val diff = end - start

                    fun Instant.toDT(
                        timeZone: TimeZone = TimeZone.currentSystemDefault()
                    ): LocalDateTime {
                        return toLocalDateTime(timeZone)
                    }

                    when {
                        diff <= 7.days -> {
                            data = points
                            overlayHeaderLabel = {
                                Text(
                                    color = MaterialTheme.colorScheme.background,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .let {
                                            it.toDT() to it.plus(1.hours).toDT()
                                        }
                                        .let {
                                            "${it.first.format(formatterHourly)} - ${it.second.format(formatterTime)}"
                                        },
                                    style = MaterialTheme.typography.labelMedium
                                )
                            }
                            xAxisLabel = {
                                Text(
                                    fontSize = 12.sp,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .toLocalDateTime(TimeZone.currentSystemDefault())
                                        .format(formatterHourlyLabelX),
                                    textAlign = TextAlign.Center
                                )
                            }
                        }

                        diff <= 14.days -> {
                            data = points.groupBy(2.hours)
                            overlayHeaderLabel = {
                                Text(
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .let {
                                            it.toDT() to it.plus(2.hours).toDT()
                                        }
                                        .let {
                                            "${it.first.format(formatterHourly)} - ${it.second.format(formatterTime)}"
                                        },
                                    color = MaterialTheme.colorScheme.background,
                                    style = MaterialTheme.typography.labelMedium
                                )
                            }

                            xAxisLabel = {
                                Text(
                                    fontSize = 12.sp,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .toLocalDateTime(TimeZone.currentSystemDefault())
                                        .date
                                        .niceFormat(false),
                                    textAlign = TextAlign.Center
                                )
                            }
                        }

                        diff <= 60.days -> {
                            data = points.groupBy(1.days)
                            overlayHeaderLabel = {
                                Text(
                                    color = MaterialTheme.colorScheme.background,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .toDT()
                                        .format(formatterDaily),
                                    style = MaterialTheme.typography.labelMedium
                                )
                            }

                            xAxisLabel = {
                                Text(
                                    fontSize = 12.sp,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .toLocalDateTime(TimeZone.currentSystemDefault())
                                        .date
                                        .niceFormat(false),
                                    textAlign = TextAlign.Center
                                )
                            }
                        }

                        else -> {
                            data = points.groupBy(7.days)
                            overlayHeaderLabel = {
                                Text(
                                    color = MaterialTheme.colorScheme.background,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .let {
                                            it.toDT() to it.plus(7.days).toDT()
                                        }
                                        .let {
                                            "${it.first.format(formatterWeekly)} - ${it.second.format(formatterDaySingle)}"
                                        },
                                    style = MaterialTheme.typography.labelMedium
                                )
                            }

                            xAxisLabel = {
                                Text(
                                    fontSize = 12.sp,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    text = Instant.fromEpochMilliseconds(it as Long)
                                        .toLocalDateTime(TimeZone.currentSystemDefault())
                                        .date
                                        .niceFormat(true),
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    class LocationOhPreview(
        val id: Long,
        val name: String,
        val stack: List<Pair<String, String>>
    )

}

private fun List<LineChartPoint>.groupBy(duration: Duration): List<LineChartPoint> {
    return buildList {
        var last = this@groupBy.first().x
        var sum = 0F
        val offset = duration.inWholeMilliseconds
        this@groupBy.forEach { point ->
            if (point.x > last + offset) {
                add(
                    LineChartPoint(
                        last,
                        sum
                    )
                )
                last = point.x
                sum = point.y
            } else {
                sum += point.y
            }
        }
        if (sum != 0F) {
            add(
                LineChartPoint(
                    last,
                    sum
                )
            )
        }
    }
}
